import type { MasterData, Permission, Role, User } from "./types";
import { positionsOf } from "./people";

export const PERMISSIONS: { id: Permission; label: string; group: string }[] = [
  { id: "order.view.all", label: "View all orders", group: "Orders" },
  { id: "order.view.own", label: "View own orders", group: "Orders" },
  { id: "order.create", label: "Create orders", group: "Orders" },
  { id: "order.assign", label: "Assign effects to staff", group: "Orders" },
  { id: "order.delete", label: "Delete codes", group: "Orders" },
  { id: "effect.work", label: "Take and work on effects", group: "Production" },
  { id: "output.upload", label: "Submit output", group: "Production" },
  { id: "qc.view.queue", label: "View QC queue", group: "Quality" },
  { id: "qc.review", label: "Pass or return output", group: "Quality" },
  { id: "delivery.review.own", label: "Review own deliveries", group: "Review" },
  { id: "chat.access", label: "Order chat", group: "Communication" },
  { id: "chat.send", label: "Send chat messages", group: "Communication" },
  { id: "user.manage", label: "View team & sync users", group: "System" },
  { id: "user.permissions.edit", label: "Edit permissions", group: "System" },
  { id: "policy.manage", label: "Edit master data", group: "System" },
  { id: "audit.view", label: "View activity log", group: "System" },
  { id: "kpi.view", label: "View KPI", group: "System" },
];

export const ROLE_LABEL: Record<Role, string> = {
  LEADER_AI: "Leader AI",
  EDITOR: "Editor",
  STAFF_AI: "Staff AI",
  OUTPUT_QC: "Output QC",
  GN: "GN (view only)",
};

/** "Editor · Staff AI" for someone with several positions. */
export function roleLabels(u: { role: Role | null; roles?: Role[] } | null | undefined): string {
  const list = u?.roles?.length ? u.roles : u?.role ? [u.role] : [];
  return list.map((r) => ROLE_LABEL[r]).join(" · ");
}

/** Role text after a name in chat: nothing for the Admin vận hành team (they hold every role), the role for others. */
export function chatRoleLabel(u: { role: Role | null; roles?: Role[]; teamIds?: string[] } | null | undefined): string {
  if (!u?.role || u.teamIds?.includes("ADMIN-OPS")) return "";
  return ` · ${roleLabels(u)}`;
}

export const ROLE_DEFAULT_PERMISSIONS: Record<Role, Permission[]> = {
  LEADER_AI: PERMISSIONS.map((p) => p.id),
  EDITOR: ["order.create", "order.view.own", "delivery.review.own", "chat.access", "chat.send"],
  STAFF_AI: ["effect.work", "output.upload", "chat.access", "chat.send"],
  OUTPUT_QC: ["qc.view.queue", "qc.review", "chat.access", "chat.send"],
  GN: ["order.view.all", "chat.access"],
};

export const ROLE_LANDING: Record<Role, string> = {
  LEADER_AI: "/control-tower",
  EDITOR: "/home",
  STAFF_AI: "/workbench",
  OUTPUT_QC: "/qc",
  GN: "/orders",
};

type Subject = (Pick<User, "role" | "status" | "permissionsOverride"> & Partial<Pick<User, "positions" | "email" | "teamId" | "namecode">>) | null | undefined;

export function effectivePermissions(user: Subject): Permission[] {
  if (!user || user.status !== "ACTIVE" || !user.role) return [];
  if (user.permissionsOverride) return user.permissionsOverride;
  // Someone with several positions gets what each of them allows.
  return [...new Set(positionsOf(user).flatMap((p) => ROLE_DEFAULT_PERMISSIONS[p.role]))];
}

export function hasPermission(user: Subject, permission: Permission): boolean {
  return effectivePermissions(user).includes(permission);
}

/** A requirement is one permission, or a list where any one is enough. */
export type Requirement = Permission | Permission[] | null;

export function satisfies(user: Subject, req: Requirement): boolean {
  if (req === null) return !!user && user.status === "ACTIVE";
  const list = Array.isArray(req) ? req : [req];
  return list.some((p) => hasPermission(user, p));
}

export function landingFor(user: Subject): string {
  if (!user || !user.role) return "/login";
  return ROLE_LANDING[user.role];
}

export class ForbiddenError extends Error {
  readonly permission: Requirement;
  constructor(permission: Requirement, message = "You do not have permission to do this.") {
    super(message);
    this.name = "ForbiddenError";
    this.permission = permission;
  }
}

export function assertPermission(user: Subject, req: Requirement): void {
  if (!satisfies(user, req)) throw new ForbiddenError(req);
}

const isActiveStaff = (user: Subject) => !!user && user.status === "ACTIVE" && positionsOf(user).some((p) => p.role === "STAFF_AI");
type ResourceSettings = Pick<MasterData, "staffCanViewResources" | "staffCanEditResources"> | undefined;

/** Purchased resources: whoever edits master data, and Staff AI when master data allows it. */
export function canViewResources(user: Subject, md: ResourceSettings): boolean {
  if (hasPermission(user, "policy.manage")) return true;
  return !!md?.staffCanViewResources && isActiveStaff(user);
}

/** Adding, editing and deleting purchases: the Leader, and Staff AI when master data says "View and edit". */
export function canEditResources(user: Subject, md: ResourceSettings): boolean {
  if (hasPermission(user, "policy.manage")) return true;
  return !!md?.staffCanViewResources && !!md.staffCanEditResources && isActiveStaff(user);
}
